import { getTenantByHost, getTenantId, TenantId, tenants } from '@pelicargo/tenants';
import { logout } from '@pelicargo/ui';
import { PermissionLevel } from '@pelicargo/utils';
import { Contact } from '@prisma/client';
import * as Sentry from '@sentry/react';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { supabase } from '../config/supabase';
import { trpc, TrpcOutputs } from '../config/trpc';
import { useContacts } from '../hooks/contact/useContacts';

type User = NonNullable<TrpcOutputs['currentUser']>['user'];

interface Context {
  isLoggedIn: boolean;
  token?: string;
  permission?: PermissionLevel;
  user: User;
  isLoading?: boolean;
  logout: () => void;
  defaultContact?: Contact;
  knownTenants: TenantId[];
}

export const AuthContext = createContext({} as Context);

interface Props {
  children: ReactNode;
}

export const AuthProvider = ({ children }: Props) => {
  const [token, setToken] = useLocalStorage<string>('accessToken', '');
  const [user, setUser] = useLocalStorage<User | null>('user', null);

  const { data } = trpc.currentUser.useQuery(undefined, { refetchOnWindowFocus: false, enabled: !!token });

  const [contacts] = useContacts({ minPermission: PermissionLevel.CSMEMBER, tenant: getTenantId() });

  const defaultContact = useMemo(() => {
    return contacts?.find((contact) => contact?.email === user?.email);
  }, [contacts, user?.email]);

  const knownTenants = useMemo(() => {
    if (data?.highestPermission === PermissionLevel.SUPERADMIN || getTenantByHost().isSystemTenant) {
      return Object.keys(tenants) as TenantId[];
    }
    return Array.from(new Set(data?.user?.account_permissions?.map(({ tenant_id }) => tenant_id) ?? []));
  }, [data?.highestPermission, data?.user?.account_permissions]);

  const handleUpdateUser = useCallback(
    (user: User) => {
      if (!user) return;
      setUser(user);
      Sentry.setUser({ id: user?.id, fullName: user?.full_name });
    },
    [setUser],
  );

  useEffect(() => {
    handleUpdateUser(data?.user);
  }, [data, handleUpdateUser]);

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') setToken(session?.access_token);
      if (event === 'TOKEN_REFRESHED') setToken(session?.access_token);
      if (event === 'SIGNED_OUT') logout();
    });

    return () => listener.subscription.unsubscribe();
  }, [setToken]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!token,
        user,
        token,
        permission: data?.highestPermission,
        logout,
        defaultContact,
        knownTenants,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
